import React, { createContext, useContext, useReducer, useMemo, useCallback, useEffect } from 'react'
import { client } from '../apollo/client'
import {
  TOKEN_DATA,
  FILTERED_TRANSACTIONS,
  TOKEN_CHART,
  TOKENS_CURRENT,
  TOKENS_DYNAMIC,
  PRICES_BY_BLOCK,
  DAILY_TOKEN_STATS,
} from '../apollo/queries'
import { fetchHourlyPairVolumes } from '../utils/hourlyStats'
import useTransactionFeed from '../hooks/useTransactionFeed'

import { useNrgPrice, useGlobalData } from './GlobalData'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

import {
  get2DayPercentChange,
  getPercentChange,
  getBlockFromTimestamp,
  isAddress,
  getBlocksFromTimestamps,
  splitQuery,
} from '../utils'
import getEnvironmentVar from '../utils/getEnvironmentVar'
import { timeframeOptions } from '../constants'
import { useLatestBlocks } from './Application'

const WNRG_ADDRESS = getEnvironmentVar('WNRG_ADDRESS')
const CHARTS_START_TIME = getEnvironmentVar('CHARTS_START_TIME')

const UPDATE = 'UPDATE'
const UPDATE_CHART_DATA = 'UPDATE_CHART_DATA'
const UPDATE_PRICE_DATA = 'UPDATE_PRICE_DATA'
const UPDATE_TOP_TOKENS = ' UPDATE_TOP_TOKENS'
const UPDATE_ALL_PAIRS = 'UPDATE_ALL_PAIRS'

const TOKEN_PAIRS_KEY = 'TOKEN_PAIRS_KEY'

dayjs.extend(utc)

const TokenDataContext = createContext()

function useTokenDataContext() {
  return useContext(TokenDataContext)
}

function reducer(state, { type, payload }) {
  switch (type) {
    case UPDATE: {
      const { tokenAddress, data } = payload
      return {
        ...state,
        [tokenAddress]: {
          ...state?.[tokenAddress],
          ...data,
        },
      }
    }
    case UPDATE_TOP_TOKENS: {
      const { topTokens } = payload
      let added = {}
      topTokens &&
        topTokens.map((token) => {
          return (added[token.id] = token)
        })
      return {
        ...state,
        ...added,
      }
    }

    case UPDATE_CHART_DATA: {
      const { address, chartData } = payload
      return {
        ...state,
        [address]: {
          ...state?.[address],
          chartData,
        },
      }
    }

    case UPDATE_PRICE_DATA: {
      const { address, data, timeWindow, interval } = payload
      return {
        ...state,
        [address]: {
          ...state?.[address],
          [timeWindow]: {
            ...state?.[address]?.[timeWindow],
            [interval]: data,
          },
        },
      }
    }

    case UPDATE_ALL_PAIRS: {
      const { address, allPairs } = payload
      return {
        ...state,
        [address]: {
          ...state?.[address],
          [TOKEN_PAIRS_KEY]: allPairs,
        },
      }
    }
    default: {
      throw Error(`Unexpected action type in DataContext reducer: '${type}'.`)
    }
  }
}

export default function Provider({ children }) {
  const [state, dispatch] = useReducer(reducer, {})
  const update = useCallback((tokenAddress, data) => {
    dispatch({
      type: UPDATE,
      payload: {
        tokenAddress,
        data,
      },
    })
  }, [])

  const updateTopTokens = useCallback((topTokens) => {
    dispatch({
      type: UPDATE_TOP_TOKENS,
      payload: {
        topTokens,
      },
    })
  }, [])

  const updateChartData = useCallback((address, chartData) => {
    dispatch({
      type: UPDATE_CHART_DATA,
      payload: { address, chartData },
    })
  }, [])

  const updateAllPairs = useCallback((address, allPairs) => {
    dispatch({
      type: UPDATE_ALL_PAIRS,
      payload: { address, allPairs },
    })
  }, [])

  const updatePriceData = useCallback((address, data, timeWindow, interval) => {
    dispatch({
      type: UPDATE_PRICE_DATA,
      payload: { address, data, timeWindow, interval },
    })
  }, [])

  return (
    <TokenDataContext.Provider
      value={useMemo(
        () => [
          state,
          {
            update,
            updateChartData,
            updateTopTokens,
            updateAllPairs,
            updatePriceData,
          },
        ],
        [state, update, updateChartData, updateTopTokens, updateAllPairs, updatePriceData]
      )}
    >
      {children}
    </TokenDataContext.Provider>
  )
}

const getTopTokens = async (nrgPrice, nrgPriceOld, maxTokenLiquidity) => {
  const utcCurrentTime = dayjs()
  const utcOneDayBack = utcCurrentTime.subtract(1, 'day').unix()
  const utcTwoDaysBack = utcCurrentTime.subtract(2, 'day').unix()
  let oneDayBlock = await getBlockFromTimestamp(utcOneDayBack)
  let twoDayBlock = await getBlockFromTimestamp(utcTwoDaysBack)

  try {
    let current = await client.query({
      query: TOKENS_CURRENT,
      fetchPolicy: 'cache-first',
    })

    let oneDayResult = await client.query({ query: TOKENS_DYNAMIC(oneDayBlock), fetchPolicy: 'cache-first' }).catch(() => ({ data: { tokens: [] } }))
    let twoDayResult = await client.query({ query: TOKENS_DYNAMIC(twoDayBlock), fetchPolicy: 'cache-first' }).catch(() => ({ data: { tokens: [] } }))

    let oneDayStatistic = oneDayResult?.data?.tokens.reduce((obj, cur, i) => {
      return { ...obj, [cur.id]: cur }
    }, {})

    let twoDayStatistic = twoDayResult?.data?.tokens.reduce((obj, cur, i) => {
      return { ...obj, [cur.id]: cur }
    }, {})

    // Per-token volumes derived from hourly pair stats (each token gets full pair swap volume,
    // matching production's methodology — indexer's hourlyTokenStatistics doesn't exist)
    const { tokenVolumeMap, prevTokenVolumeMap } = await fetchHourlyPairVolumes().catch(() => ({ tokenVolumeMap: {}, prevTokenVolumeMap: {} }))

    // Daily token stats used only for recent-activity detection (UBT sanity check)
    const nowSec = dayjs.utc().unix()
    const todayMidnight = Math.floor(nowSec / 86400) * 86400
    const threeDaysAgoMidnight = String(todayMidnight - 3 * 86400)
    const dailyTokenStatsResult = await client.query({
      query: DAILY_TOKEN_STATS,
      variables: { dateFrom: threeDaysAgoMidnight },
      fetchPolicy: 'cache-first',
    }).catch(() => ({ data: { dailyTokenStatistics: [] } }))

    const dailyTokenMap = {}
    for (const stat of dailyTokenStatsResult?.data?.dailyTokenStatistics ?? []) {
      const tid = stat.token.id
      if (!dailyTokenMap[tid]) dailyTokenMap[tid] = []
      dailyTokenMap[tid].push(stat)
    }

    let bulkResults = await Promise.all(
      current &&
        oneDayStatistic &&
        twoDayStatistic &&
        current?.data?.tokens.map(async (token) => {
          let data = token

          let oneDayHistory = oneDayStatistic?.[token.id]
          let twoDayHistory = twoDayStatistic?.[token.id]

          // catch the case where token wasnt in top list in previous days
          if (!oneDayHistory) {
            let oneDayResult = await client.query({ query: TOKEN_DATA(token.id, oneDayBlock), fetchPolicy: 'cache-first' }).catch(() => ({ data: { tokens: [] } }))
            oneDayHistory = oneDayResult.data.tokens[0]
          }
          if (!twoDayHistory) {
            let twoDayResult = await client.query({ query: TOKEN_DATA(token.id, twoDayBlock), fetchPolicy: 'cache-first' }).catch(() => ({ data: { tokens: [] } }))
            twoDayHistory = twoDayResult.data.tokens[0]
          }

          // calculate percentage changes and daily changes
          const [oneDayVolumeUSD, volumeChangeUSD] = get2DayPercentChange(
            data.tradeVolumeUSD,
            oneDayHistory?.tradeVolumeUSD ?? 0,
            twoDayHistory?.tradeVolumeUSD ?? 0
          )
          const [oneDayTxns, txnChange] = get2DayPercentChange(
            data.txCount,
            oneDayHistory?.txCount ?? 0,
            twoDayHistory?.txCount ?? 0
          )

          const currentLiquidityUSD = data?.totalLiquidity * nrgPrice * data?.derivedNRG
          const oldLiquidityUSD = oneDayHistory?.totalLiquidity * nrgPriceOld * oneDayHistory?.derivedNRG

          // percent changes — use yesterday's daily stat priceUSD as 24h-ago baseline
          // (block-based oneDayHistory is unavailable on the new indexer)
          const yesterdayPriceUSD = parseFloat(dailyTokenMap[token.id]?.[1]?.priceUSD ?? 0)
          const priceChangeUSD = getPercentChange(
            data?.derivedNRG * nrgPrice,
            yesterdayPriceUSD > 0 ? yesterdayPriceUSD : (oneDayHistory?.derivedNRG ? oneDayHistory?.derivedNRG * nrgPriceOld : 0)
          )

          // set data
          data.priceUSD = data?.derivedNRG * nrgPrice
          // A single token cannot have more liquidity than the entire DEX.
          // If it does, derivedNRG or totalLiquidity is wrong in the indexer → zero it out.
          const liquidityIsSane = !maxTokenLiquidity || currentLiquidityUSD <= maxTokenLiquidity
          data.totalLiquidityUSD = liquidityIsSane ? currentLiquidityUSD : 0
          data.oneDayVolumeUSD = parseFloat(oneDayVolumeUSD)
          data.volumeChangeUSD = volumeChangeUSD
          data.priceChangeUSD = priceChangeUSD
          data.liquidityChangeUSD = getPercentChange(currentLiquidityUSD ?? 0, oldLiquidityUSD ?? 0)
          data.oneDayTxns = oneDayTxns
          data.txnChange = txnChange

          // When no block history, use token volumes derived from hourly pair stats
          if (!oneDayHistory) {
            const current24h = tokenVolumeMap[token.id] ?? 0
            const prev24h = prevTokenVolumeMap[token.id] ?? 0
            data.oneDayVolumeUSD = current24h
            data.volumeChangeUSD = getPercentChange(current24h, prev24h)
            data.oneDayTxns = 0
            data.txnChange = 0
          }

          // fix for WNRG
          if (data.id === WNRG_ADDRESS) {
            data.name = 'Energi'
            data.symbol = 'NRG'
          }

          return data
        })
    )

    return bulkResults

    // calculate percentage changes and daily changes
  } catch (e) {
    console.log(e)
  }
}

const getTokenData = async (address, nrgPrice, nrgPriceOld) => {
  const utcCurrentTime = dayjs()
  const utcOneDayBack = utcCurrentTime.subtract(1, 'day').startOf('minute').unix()
  const utcTwoDaysBack = utcCurrentTime.subtract(2, 'day').startOf('minute').unix()
  let oneDayBlock = await getBlockFromTimestamp(utcOneDayBack)
  let twoDayBlock = await getBlockFromTimestamp(utcTwoDaysBack)

  // initialize data arrays
  let data = {}
  let oneDayStatistic = {}
  let twoDayStatistic = {}

  try {
    // fetch all current and historical data
    let result = await client.query({
      query: TOKEN_DATA(address),
      fetchPolicy: 'cache-first',
    })
    data = result?.data?.tokens?.[0]

    // get results from 24 hours in past
    let oneDayResult = await client.query({
      query: TOKEN_DATA(address, oneDayBlock),
      fetchPolicy: 'cache-first',
    })
    oneDayStatistic = oneDayResult.data.tokens[0]

    // get results from 48 hours in past
    let twoDayResult = await client.query({
      query: TOKEN_DATA(address, twoDayBlock),
      fetchPolicy: 'cache-first',
    })
    twoDayStatistic = twoDayResult.data.tokens[0]

    // catch the case where token wasnt in top list in previous days
    if (!oneDayStatistic) {
      let oneDayResult = await client.query({
        query: TOKEN_DATA(address, oneDayBlock),
        fetchPolicy: 'cache-first',
      })
      oneDayStatistic = oneDayResult.data.tokens[0]
    }
    if (!twoDayStatistic) {
      let twoDayResult = await client.query({
        query: TOKEN_DATA(address, twoDayBlock),
        fetchPolicy: 'cache-first',
      })
      twoDayStatistic = twoDayResult.data.tokens[0]
    }

    // calculate percentage changes and daily changes
    const [oneDayVolumeUSD, volumeChangeUSD] = get2DayPercentChange(
      data.tradeVolumeUSD,
      oneDayStatistic?.tradeVolumeUSD ?? 0,
      twoDayStatistic?.tradeVolumeUSD ?? 0
    )

    // calculate percentage changes and daily changes
    const [oneDayVolumeUT, volumeChangeUT] = get2DayPercentChange(
      data.untrackedVolumeUSD,
      oneDayStatistic?.untrackedVolumeUSD ?? 0,
      twoDayStatistic?.untrackedVolumeUSD ?? 0
    )

    // calculate percentage changes and daily changes
    const [oneDayTxns, txnChange] = get2DayPercentChange(
      data.txCount,
      oneDayStatistic?.txCount ?? 0,
      twoDayStatistic?.txCount ?? 0
    )

    const priceChangeUSD = getPercentChange(
      data?.derivedNRG * nrgPrice,
      parseFloat(oneDayStatistic?.derivedNRG ?? 0) * nrgPriceOld
    )

    const currentLiquidityUSD = data?.totalLiquidity * nrgPrice * data?.derivedNRG
    const oldLiquidityUSD = oneDayStatistic?.totalLiquidity * nrgPriceOld * oneDayStatistic?.derivedNRG

    // set data
    data.priceUSD = data?.derivedNRG * nrgPrice
    data.totalLiquidityUSD = currentLiquidityUSD
    data.oneDayVolumeUSD = oneDayVolumeUSD
    data.volumeChangeUSD = volumeChangeUSD
    data.priceChangeUSD = priceChangeUSD
    data.oneDayVolumeUT = oneDayVolumeUT
    data.volumeChangeUT = volumeChangeUT
    const liquidityChangeUSD = getPercentChange(currentLiquidityUSD ?? 0, oldLiquidityUSD ?? 0)
    data.liquidityChangeUSD = liquidityChangeUSD
    data.oneDayTxns = oneDayTxns
    data.txnChange = txnChange

    // new tokens
    if (!oneDayStatistic && data) {
      data.oneDayVolumeUSD = data.tradeVolumeUSD
      data.oneDayVolumeNRG = data.tradeVolume * data.derivedNRG
      data.oneDayTxns = data.txCount
    }

    // fix for WNRG
    if (data.id === WNRG_ADDRESS) {
      data.name = 'Energi (Wrapped)'
      data.symbol = 'NRG'
    }
  } catch (e) {
    console.log(e)
  }
  return data
}

const getTokenTransactions = (allPairsFormatted) => async (windowStart, windowEnd, first, direction = 'desc') => {
  const transactions = {}
  try {
    let result = await client.query({
      query: FILTERED_TRANSACTIONS,
      variables: {
        allPairs: allPairsFormatted,
        first,
        windowStart: String(windowStart),
        windowEnd: String(windowEnd),
        orderDirection: direction,
      },
      fetchPolicy: 'cache-first',
    })
    transactions.mints = result.data.mints
    transactions.burns = result.data.burns
    transactions.swaps = result.data.swaps
  } catch (e) {
    console.log(e)
  }
  return transactions
}

const getTokenPairs = async (tokenAddress) => {
  try {
    // fetch all current and historical data
    let result = await client.query({
      query: TOKEN_DATA(tokenAddress),
      fetchPolicy: 'cache-first',
    })
    return result.data?.['pairs0'].concat(result.data?.['pairs1'])
  } catch (e) {
    console.log(e)
  }
}

const getIntervalTokenData = async (tokenAddress, startTime, interval = 3600, latestBlock) => {
  const utcEndTime = dayjs.utc()
  let time = startTime

  // create an array of hour start times until we reach current hour
  // buffer by half hour to catch case where graph isnt synced to latest block
  const timestamps = []
  while (time < utcEndTime.unix()) {
    timestamps.push(time)
    time += interval
  }

  // backout if invalid timestamp format
  if (timestamps.length === 0) {
    return []
  }

  // once you have all the timestamps, get the blocks for each timestamp in a bulk query
  let blocks
  try {
    blocks = await getBlocksFromTimestamps(timestamps, 100)

    // catch failing case
    if (!blocks || blocks.length === 0) {
      return []
    }

    if (latestBlock) {
      blocks = blocks.filter((b) => {
        return parseFloat(b.number) <= parseFloat(latestBlock)
      })
    }

    let result = await splitQuery(PRICES_BY_BLOCK, client, [tokenAddress], blocks, 50)

    // format token NRG price results
    let values = []
    for (var row in result) {
      let timestamp = row.split('t')[1]
      let derivedNRG = parseFloat(result[row]?.derivedNRG)
      if (timestamp) {
        values.push({
          timestamp,
          derivedNRG,
        })
      }
    }

    // go through NRG usd prices and assign to original values array
    let index = 0
    for (var brow in result) {
      let timestamp = brow.split('b')[1]
      if (timestamp && result[brow]) {
        values[index].nrgUsdPrice = values[index].derivedNRG * result[brow].nrgUsdPrice
        index += 1
      }
    }

    let formattedHistory = []

    // for each hour, construct the open and close price
    for (let i = 0; i < values.length - 1; i++) {
      formattedHistory.push({
        timestamp: values[i].timestamp,
        open: parseFloat(values[i].nrgUsdPrice),
        close: parseFloat(values[i + 1].nrgUsdPrice),
      })
    }

    return formattedHistory
  } catch (e) {
    console.log(e)
    console.log('error fetching blocks')
    return []
  }
}

const getTokenChartData = async (tokenAddress) => {
  let data = []
  const utcEndTime = dayjs.utc()
  let utcStartTime = utcEndTime.subtract(1, 'year')
  let startTime = utcStartTime.startOf('minute').unix() - 1

  try {
    let allFound = false
    let skip = 0
    while (!allFound) {
      let result = await client.query({
        query: TOKEN_CHART,
        variables: {
          tokenAddr: tokenAddress,
          skip,
        },
        fetchPolicy: 'cache-first',
      })
      if (result.data.dailyTokenStatistics.length < 1000) {
        allFound = true
      }
      skip += 1000
      data = data.concat(result.data.dailyTokenStatistics)
    }

    let dayIndexSet = new Set()
    let dayIndexArray = []
    const oneDay = 24 * 60 * 60
    data.forEach((dailyStatistic, i) => {
      // add the day index to the set of days
      dayIndexSet.add((data[i].date / oneDay).toFixed(0))
      dayIndexArray.push(data[i])
      dailyStatistic.dailyVolumeUSD = parseFloat(dailyStatistic.dailyVolumeUSD)
    })

    // fill in empty days
    let timestamp = data[0] && data[0].date ? data[0].date : startTime
    let latestLiquidityUSD = data[0] && data[0].totalLiquidityUSD
    let latestPriceUSD = data[0] && data[0].priceUSD
    let latestPairDatas = data[0] && data[0].mostLiquidPairs
    let index = 1
    while (timestamp < utcEndTime.startOf('minute').unix() - oneDay) {
      const nextDay = timestamp + oneDay
      let currentDayIndex = (nextDay / oneDay).toFixed(0)
      if (!dayIndexSet.has(currentDayIndex)) {
        data.push({
          date: nextDay,
          dayString: nextDay,
          dailyVolumeUSD: 0,
          priceUSD: latestPriceUSD,
          totalLiquidityUSD: latestLiquidityUSD,
          mostLiquidPairs: latestPairDatas,
        })
      } else {
        latestLiquidityUSD = dayIndexArray[index].totalLiquidityUSD
        latestPriceUSD = dayIndexArray[index].priceUSD
        latestPairDatas = dayIndexArray[index].mostLiquidPairs
        index = index + 1
      }
      timestamp = nextDay
    }
    data = data.sort((a, b) => (parseInt(a.date) > parseInt(b.date) ? 1 : -1))
  } catch (e) {
    console.log(e)
  }
  return data
}

export function Updater() {
  const [, { updateTopTokens }] = useTokenDataContext()
  const [nrgPrice, nrgPriceOld] = useNrgPrice()
  const { totalLiquidityUSD } = useGlobalData()
  useEffect(() => {
    async function getData() {
      // get top pairs for overview list
      let topTokens = await getTopTokens(nrgPrice, nrgPriceOld, totalLiquidityUSD)
      topTokens && updateTopTokens(topTokens)
    }
    nrgPrice && nrgPriceOld && getData()
  }, [nrgPrice, nrgPriceOld, totalLiquidityUSD, updateTopTokens])
  return null
}

export function useTokenData(tokenAddress) {
  const [state, { update }] = useTokenDataContext()
  const [nrgPrice, nrgPriceOld] = useNrgPrice()
  const tokenData = state?.[tokenAddress]

  useEffect(() => {
    if (!tokenData && nrgPrice && nrgPriceOld && isAddress(tokenAddress)) {
      getTokenData(tokenAddress, nrgPrice, nrgPriceOld).then((data) => {
        update(tokenAddress, data)
      })
    }
  }, [nrgPrice, nrgPriceOld, tokenAddress, tokenData, update])

  return tokenData || {}
}

export function useTokenTransactions(tokenAddress) {
  const [state] = useTokenDataContext()

  const allPairsFormatted =
    state[tokenAddress] &&
    state[tokenAddress].TOKEN_PAIRS_KEY &&
    state[tokenAddress].TOKEN_PAIRS_KEY.map((pair) => {
      return pair.id
    })

  return useTransactionFeed(
    getTokenTransactions(allPairsFormatted),
    allPairsFormatted ? tokenAddress : undefined
  )
}

export function useTokenPairs(tokenAddress) {
  const [state, { updateAllPairs }] = useTokenDataContext()
  const tokenPairs = state?.[tokenAddress]?.[TOKEN_PAIRS_KEY]

  useEffect(() => {
    async function fetchData() {
      let allPairs = await getTokenPairs(tokenAddress)
      updateAllPairs(tokenAddress, allPairs)
    }
    if (!tokenPairs && isAddress(tokenAddress)) {
      fetchData()
    }
  }, [tokenAddress, tokenPairs, updateAllPairs])

  return tokenPairs || []
}

export function useTokenChartData(tokenAddress) {
  const [state, { updateChartData }] = useTokenDataContext()
  const chartData = state?.[tokenAddress]?.chartData
  useEffect(() => {
    async function checkForChartData() {
      if (!chartData) {
        let data = await getTokenChartData(tokenAddress)
        updateChartData(tokenAddress, data)
      }
    }
    checkForChartData()
  }, [chartData, tokenAddress, updateChartData])
  return chartData
}

/**
 * get candlestick data for a token - saves in context based on the window and the
 * interval size
 * @param {*} tokenAddress
 * @param {*} timeWindow // a preset time window from constant - how far back to look
 * @param {*} interval  // the chunk size in seconds - default is 1 hour of 3600s
 */
export function useTokenPriceData(tokenAddress, timeWindow, interval = 3600) {
  const [state, { updatePriceData }] = useTokenDataContext()
  const chartData = state?.[tokenAddress]?.[timeWindow]?.[interval]
  const [latestBlock] = useLatestBlocks()

  useEffect(() => {
    const currentTime = dayjs.utc()
    const windowSize = timeWindow === timeframeOptions.MONTH ? 'month' : 'week'
    const startTime =
      timeWindow === timeframeOptions.ALL_TIME
        ? parseInt(CHARTS_START_TIME, 10)
        : Math.max(currentTime.subtract(1, windowSize).startOf('hour').unix(), CHARTS_START_TIME)

    async function fetch() {
      let data = await getIntervalTokenData(tokenAddress, startTime, interval, latestBlock)
      updatePriceData(tokenAddress, data, timeWindow, interval)
    }
    if (!chartData) {
      fetch()
    }
  }, [chartData, interval, timeWindow, tokenAddress, updatePriceData, latestBlock])

  return chartData
}

export function useAllTokenData() {
  const [state] = useTokenDataContext()
  return state
}
